// Response schemas for the endpoints the client calls, built on the shared role and permission
// lists so web and (phase 2) mobile parse the same contract (plan §4). Unknown keys are dropped.
import { PERMISSIONS, ROLES, type Permission } from '@cis/shared';
import { z } from 'zod';

const MAX_ERROR_CODE = 64;
const MAX_ERROR_MESSAGE = 1000;
const MIN_EMAIL = 3;
const MAX_EMAIL = 320; // RFC 5321 path limit
const MAX_DISPLAY_NAME = 200;
const MAX_PACK_VERSION = 64;

const KNOWN_PERMISSIONS: ReadonlySet<string> = new Set(PERMISSIONS);

function isPermission(value: string): value is Permission {
  return KNOWN_PERMISSIONS.has(value);
}

export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string().min(1).max(MAX_ERROR_CODE),
    message: z.string().max(MAX_ERROR_MESSAGE),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});

// GET /auth/nonce (plan §4, amended 04-Nov): random, single use, base64url, 16–256 characters.
// Matches "Zm9vYmFyYmF6cXV4LTEyMzQ1Njc4"; rejects "short", "has space in it here", "a+b/c=...".
// https://regex101.com/?regex=%5E%5BA-Za-z0-9_-%5D%7B16%2C256%7D%24&testString=Zm9vYmFyYmF6cXV4LTEyMzQ1Njc4%0Ashort&flavor=javascript
const NONCE_FORMAT = /^[A-Za-z0-9_-]{16,256}$/;

export const nonceSchema = z.object({ nonce: z.string().regex(NONCE_FORMAT) });

export const meUserSchema = z.object({
  id: z.number().int().positive(),
  role: z.enum(ROLES),
  email: z.string().min(MIN_EMAIL).max(MAX_EMAIL),
  displayName: z.string().min(1).max(MAX_DISPLAY_NAME),
});

// GET /me and a successful POST /auth/session. A permission this build does not know is dropped
// rather than failing sign-in: the menu then simply does not offer it (fails closed).
export const meSchema = z.object({
  user: meUserSchema,
  permissions: z.array(z.string()).transform((list) => list.filter(isPermission)),
  packVersion: z.string().min(1).max(MAX_PACK_VERSION).nullable(),
});

export type Nonce = z.infer<typeof nonceSchema>;
export type MeUser = z.infer<typeof meUserSchema>;
export type Me = z.infer<typeof meSchema>;
